import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styles from './index.module.scss';

import actions from '@/actions';
import AccordionCaret from '@/components/ui/AccordionCaret';
import { CommandButton } from '@/components/ui/kit/Button';
import commandSet from '@/define/commandSet';
import { NotifierService as notifier } from '@/libs/Notifier';
import MODELS from '@/models';

const cx = classNames.bind(styles);

const Command = () => {
  const dispatch = useDispatch();
  const robots = useSelector((state) => state.robots);
  const targetRobotIds = useSelector((state) => state.robot.targetRobotIds);
  const { sidebarCommandId, overlayCommandId } = useSelector((state) => state.command);
  const [isExpanded, setIsExpanded] = useState({});
  const containerRef = useRef(null);
  const panelRefs = useRef({});

  const targetRobots = useMemo(() => {
    return robots.filter(({ id }) => targetRobotIds.includes(id));
  }, [robots, targetRobotIds]);

  const modelId = useMemo(() => {
    if (targetRobotIds.length === 0) return;

    const modelIds = robots.filter((robot) => targetRobotIds.includes(robot.id)).map((robot) => robot.model.id);
    const uniqueModelIds = [...new Set(modelIds)];
    if (uniqueModelIds.length > 1) return;

    return uniqueModelIds[0];
  }, [targetRobotIds]);

  const groups = useMemo(() => {
    return MODELS[modelId]?.commandGroups.operation;
  }, [modelId]);

  useUpdateEffect(() => {
    if (!sidebarCommandId && !overlayCommandId) return;

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [sidebarCommandId, overlayCommandId]);

  useEffect(() => {
    if (!groups) return;

    const nextExpanded = {};
    groups.forEach(({ name }) => (nextExpanded[name] = true));
    setIsExpanded(nextExpanded);
  }, [groups]);

  const onKeyDown = (e) => {
    // Enter, Space 키 입력에 의한 클릭 효과 방지
    if (['Enter', 'Space'].includes(e.code)) {
      e.preventDefault();
    }
    // ESC 키 입력 시 닫기
    else if (e.code === 'Escape') {
      dispatch(actions.command.resetActivePanels());
    }
  };

  const toggleCommand = (e) => {
    const { commandId } = e.currentTarget.dataset;
    const command = commandSet[commandId];

    switch (command.displayMode) {
      case 'sidebar':
        dispatch(actions.command.toggleSidebar(commandId));
        break;

      case 'overlay':
        dispatch(actions.command.toggleOverlay(commandId));
        break;

      default:
        break;
    }
  };

  const toggleGroup = (groupName) => {
    setIsExpanded({
      ...isExpanded,
      [groupName]: !isExpanded[groupName],
    });
  };

  return (
    <div ref={containerRef} className={cx('container')}>
      {groups?.map((group) => {
        const groupName = group.name;
        const expanded = isExpanded[groupName];

        return (
          <div key={groupName} className={cx('group')}>
            <div className={cx('header')} onClick={() => toggleGroup(groupName)}>
              <div className={cx('title')}>{groupName}</div>
              <AccordionCaret up={expanded} size={16} />
            </div>
            <div className={cx(['body', { expanded }])}>
              {group.commands.map((command, commandIndex) => {
                const { Icon, Panel } = command;
                const isPressed = [sidebarCommandId, overlayCommandId].includes(command.id);
                const isInSidebar = command.displayMode === 'sidebar';

                // 명령 비활성화 조건:
                // 1. singleRobotOnly 명령인데 여러 대의 로봇이 선택된 경우
                // 2. commanderOnly 명령인데 권한이 없는 로봇이 포함된 경우
                const isDisabled =
                  (command.singleRobotOnly && targetRobotIds.length > 1) ||
                  (command.commanderOnly && !targetRobots.every(({ isOwned }) => isOwned));

                return (
                  <div key={commandIndex} className={cx('command')}>
                    <CommandButton
                      Icon={Icon}
                      text={command.name}
                      pressed={isPressed}
                      disabled={isDisabled}
                      onClick={toggleCommand}
                      data-command-id={command.id}
                    />
                    {isPressed && isInSidebar && (
                      <div ref={(element) => (panelRefs.current[command.id] = element)} className={cx('panel')}>
                        <Panel data={targetRobots} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Command;
